import React, { useState, useEffect } from 'react';
import { ProgressCircleProps, PROGRESS_CIRCLE_DEFAULTS } from './ProgressCircle.def';
import './ProgressCircle.css';

/**
 * Circular progress indicator component
 * Displays a ring with progress arc and optional percentage text
 * Animates from 0 to target progress on mount
 */
const ProgressCircle: React.FC<ProgressCircleProps> = ({
    progress,
    size = PROGRESS_CIRCLE_DEFAULTS.size,
    strokeWidth = PROGRESS_CIRCLE_DEFAULTS.strokeWidth,
    color = PROGRESS_CIRCLE_DEFAULTS.color,
    showPercentage = PROGRESS_CIRCLE_DEFAULTS.showPercentage,
    className = '',
}) => {
    // Animated progress - starts at 0 and animates to target
    const [animatedProgress, setAnimatedProgress] = useState(0);

    useEffect(() => {
        // Use a timeout to ensure the transition is visible on mount
        const timer = setTimeout(() => setAnimatedProgress(progress), 100);
        return () => clearTimeout(timer);
    }, [progress]);

    // Clamp progress between 0 and 100
    const clampedProgress = Math.min(100, Math.max(0, animatedProgress));

    // Calculate circle properties
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;

    // Calculate font size based on circle size
    const fontSize = size * 0.22;

    return (
        <div
            className={`progress-circle-container ${className}`}
            style={{ width: size, height: size }}
        >
            <svg
                className="progress-circle-svg"
                width={size}
                height={size}
            >
                {/* Background circle */}
                <circle
                    className="progress-circle-bg"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    strokeWidth={strokeWidth}
                />

                {/* Progress arc */}
                <circle
                    className="progress-circle-fg"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                />
            </svg>

            {/* Percentage text */}
            {showPercentage && (
                <div
                    className="progress-circle-text"
                    style={{ fontSize: `${fontSize}px` }}
                >
                    {Math.round(clampedProgress)}%
                </div>
            )}
        </div>
    );
};

export default ProgressCircle;
